import { Controller } from "@hotwired/stimulus"

// Drives the floating HUD : open/close, switch tabs, poll stats every minute,
// submit the inline "quick task" form, drag the bubble around (position kept
// in localStorage), hide it entirely (and surface a tiny restore handle).
//
// LocalStorage keys :
//   - hud_position : "{left, top}" in px (numeric origin top-left of viewport)
//   - hud_hidden   : "1" when the user dismissed the HUD
export default class extends Controller {
  static targets = ["bubble", "panel", "badge", "tachesBadge", "notifsBadge",
                    "tachesList", "notifsList", "quickForm", "quickInput", "tab",
                    "restoreHandle"]
  static values  = { statsUrl: String, quickUrl: String, pollInterval: Number }

  connect() {
    this._open = false
    this._tab  = "taches"
    this._restorePosition()
    this._restoreVisibility()

    if (this.pollIntervalValue > 0) {
      this._pollTimer = setInterval(() => this.refresh(), this.pollIntervalValue)
    }
    document.addEventListener("keydown", this._onKey = (e) => {
      if (e.key === "Escape" && this._open) this.close()
    })

    // Drag wiring : mousedown on the bubble starts a drag if the user holds
    // 200ms before moving (otherwise it's treated as a click → toggle).
    if (this.hasBubbleTarget) this._wireDrag()
  }

  disconnect() {
    if (this._pollTimer) clearInterval(this._pollTimer)
    document.removeEventListener("keydown", this._onKey)
    if (this._dragMouseDown && this.hasBubbleTarget) {
      this.bubbleTarget.removeEventListener("mousedown", this._dragMouseDown)
    }
  }

  toggle() {
    if (this._didDrag) { this._didDrag = false; return }
    this._open ? this.close() : this.open()
  }

  open() {
    this._open = true
    this.element.classList.add("hud--open")
    this.refresh()
  }

  close() {
    this._open = false
    this.element.classList.remove("hud--open")
  }

  showTab(event) {
    const tab = event.currentTarget.dataset.tab
    this._tab = tab
    this.element.querySelectorAll(".hud__tab").forEach((b) => {
      b.classList.toggle("hud__tab--active", b.dataset.tab === tab)
    })
    this.tabTargets.forEach((s) => {
      s.classList.toggle("hud__section--active", s.dataset.tab === tab)
    })
  }

  // ===== Visibility =====
  hideHud() {
    localStorage.setItem("hud_hidden", "1")
    this._restoreVisibility()
  }

  showHud() {
    localStorage.removeItem("hud_hidden")
    this._restoreVisibility()
  }

  _restoreVisibility() {
    const hidden = localStorage.getItem("hud_hidden") === "1"
    this.element.classList.toggle("hud--hidden", hidden)
    if (hidden) this.close()
  }

  // ===== Drag =====
  _wireDrag() {
    let startX = 0, startY = 0, originLeft = 0, originTop = 0, dragging = false, holdTimer = null

    const onMouseDown = (e) => {
      if (e.button !== 0) return
      const rect = this.element.getBoundingClientRect()
      startX = e.clientX; startY = e.clientY
      originLeft = rect.left; originTop = rect.top
      dragging = false
      this._didDrag = false
      holdTimer = setTimeout(() => {
        dragging = true
        this.element.classList.add("hud--dragging")
      }, 180)
      document.addEventListener("mousemove", onMouseMove)
      document.addEventListener("mouseup", onMouseUp)
    }

    const onMouseMove = (e) => {
      if (!dragging) return
      const newLeft = Math.max(8, Math.min(window.innerWidth  - 64, originLeft + (e.clientX - startX)))
      const newTop  = Math.max(8, Math.min(window.innerHeight - 64, originTop  + (e.clientY - startY)))
      this.element.style.left   = `${newLeft}px`
      this.element.style.top    = `${newTop}px`
      this.element.style.right  = "auto"
      this.element.style.bottom = "auto"
      this._didDrag = true
    }

    const onMouseUp = () => {
      clearTimeout(holdTimer)
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
      if (dragging) {
        const rect = this.element.getBoundingClientRect()
        localStorage.setItem("hud_position", JSON.stringify({ left: rect.left, top: rect.top }))
      }
      this.element.classList.remove("hud--dragging")
      dragging = false
    }

    this._dragMouseDown = onMouseDown
    this.bubbleTarget.addEventListener("mousedown", onMouseDown)
  }

  _restorePosition() {
    const raw = localStorage.getItem("hud_position")
    if (!raw) return
    try {
      const { left, top } = JSON.parse(raw)
      if (Number.isFinite(left) && Number.isFinite(top)) {
        this.element.style.left   = `${left}px`
        this.element.style.top    = `${top}px`
        this.element.style.right  = "auto"
        this.element.style.bottom = "auto"
      }
    } catch (_) { /* ignore */ }
  }

  // ===== Stats poll =====
  async refresh() {
    if (!this.statsUrlValue) return
    try {
      const res = await fetch(this.statsUrlValue, { headers: { Accept: "application/json" } })
      if (!res.ok) return
      const data = await res.json()
      this._applyStats(data)
    } catch (_e) { /* swallow */ }
  }

  _applyStats(data) {
    const tCount = (data.taches?.overdue || 0) + (data.taches?.today || 0)
    const nCount = data.notifications?.unread || 0
    const total  = tCount + nCount

    if (this.hasBadgeTarget) {
      this.badgeTarget.textContent = total > 99 ? "99+" : total
      this.badgeTarget.classList.toggle("hud__badge--hidden", total === 0)
    }
    if (this.hasTachesBadgeTarget) {
      this.tachesBadgeTarget.textContent = tCount
      this.tachesBadgeTarget.classList.toggle("hud__tab-badge--hidden", tCount === 0)
    }
    if (this.hasNotifsBadgeTarget) {
      this.notifsBadgeTarget.textContent = nCount
      this.notifsBadgeTarget.classList.toggle("hud__tab-badge--hidden", nCount === 0)
    }
  }

  async quickCreate(event) {
    event.preventDefault()
    if (!this.quickUrlValue) return
    const form = event.target
    const formData = new FormData(form)
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content

    const res = await fetch(this.quickUrlValue, {
      method: "POST",
      body: formData,
      headers: { Accept: "application/json", "X-CSRF-Token": csrf },
      credentials: "same-origin"
    })

    if (res.ok) {
      form.reset()
      if (this.hasQuickInputTarget) this.quickInputTarget.focus()
      this.refresh()
      this._flash("Tâche ajoutée", "success")
    } else {
      const json = await res.json().catch(() => ({}))
      const msg  = (json.errors || ["Erreur"]).join(", ")
      this._flash(msg, "error")
    }
  }

  _flash(message, kind) {
    const el = document.createElement("div")
    el.className = `hud__flash hud__flash--${kind}`
    el.textContent = message
    this.element.appendChild(el)
    setTimeout(() => el.classList.add("hud__flash--visible"), 10)
    setTimeout(() => { el.classList.remove("hud__flash--visible"); setTimeout(() => el.remove(), 200) }, 2200)
  }
}
